export const CHECKOUT_URL =
  'https://electrical-diagnostic-pro.impultienda.ar/checkout'

/** Every image and secondary button sends the reader to the purchase block. */
export const OFFER_ANCHOR = '#offer'

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/bXZRGJhbNT6tMKJMYyx5tK/Diseno-sin-titulo-2-4.webp'

export interface Module {
  num: number
  title: string
  body: string[]
  kicker: string
}

export const modules: Module[] = [
  {
    num: 1,
    title: 'ELECTRICAL DIAGNOSTIC FOUNDATIONS™',
    body: [
      'Access a complete visual framework for structured electrical troubleshooting including symptom verification, visual inspection, circuit analysis, test-point selection, fault isolation, root-cause confirmation and final verification.',
      'Use organized diagnostic processes to move from the observed problem toward relevant checks and evidence.',
    ],
    kicker: 'See the symptom. Follow the circuit. Verify.',
  },
  {
    num: 2,
    title: 'MULTIMETER TESTING & MEASUREMENTS™',
    body: [
      'Work with visual resources for voltage, resistance, continuity, current where appropriate, voltage drop, test-point selection and measurement interpretation.',
    ],
    kicker: 'Measure correctly. Interpret the reading. Find the next test.',
  },
  {
    num: 3,
    title: 'WIRING, CIRCUITS & CONNECTION FAULTS™',
    body: [
      'Use organized visual maps for open circuits, shorts, ground faults, loose terminals, damaged conductors, poor connections, voltage loss and high-resistance faults.',
      'Resources are structured to help trace circuit paths systematically while keeping safe procedures and system-specific information central.',
    ],
    kicker: 'Trace it. Test it. Isolate it.',
  },
  {
    num: 4,
    title: 'BREAKERS, FUSES, RELAYS, CONTACTORS & PROTECTION DEVICES™',
    body: [
      'Access visual resources for breakers, fuses, relays, contactors, switches, overload devices and control components.',
      'Use structured pathways to determine whether a device has failed or is responding to another electrical condition.',
    ],
    kicker: 'Identify. Check. Confirm. Verify.',
  },
  {
    num: 5,
    title: 'MOTORS, CONTROLS & ELECTRICAL EQUIPMENT™',
    body: [
      'Use visual pathways for motor no-start conditions, control-circuit problems, contactor operation, overload events, intermittent operation and power-side versus control-side faults.',
      'Follow organized component-testing maps and troubleshooting sequences.',
    ],
    kicker: 'Separate the circuit. Follow the evidence. Verify.',
  },
  {
    num: 6,
    title: 'REAL-WORLD TROUBLESHOOTING CASES™',
    body: [
      'Use practical diagnostic cases organized around electrical symptoms including open circuits, voltage-drop faults, protection events, control faults, wiring defects, intermittent problems and motor-related scenarios.',
      'Move directly from symptom to inspection, test points, readings, interpretation and root-cause confirmation.',
    ],
    kicker: 'See the case. Follow the tests. Diagnose.',
  },
]

export interface Bonus {
  num: number
  title: string
  subtitle: string
  body: string[]
  kicker: string
}

export const bonuses: Bonus[] = [
  {
    num: 1,
    title: 'ELECTRICAL DIAGNOSTIC QUICK REFERENCE™',
    subtitle: 'Symptom-to-Test Field Guide',
    body: [
      'Move quickly from a reported electrical symptom toward the relevant first checks, test sequence, expected results and possible diagnostic pathways.',
      'Use structured field cards, symptom matrices, measurement interpretation resources and next-test guidance alongside the main visual diagnostic system.',
    ],
    kicker: 'Find it. Check it. Test it.',
  },
  {
    num: 2,
    title: 'ELECTRICAL DIAGNOSTIC REPORTING KIT™',
    subtitle: 'Inspection, Test & Fault Documentation System',
    body: [
      'Organize diagnostic work from the reported symptom and visual inspection through test planning, measurements, fault isolation, root-cause confirmation and final verification.',
      'Keep professional troubleshooting documentation organized with practical forms, measurement logs and reusable reporting templates.',
    ],
    kicker: 'Inspect. Test. Document. Verify.',
  },
  {
    num: 3,
    title: 'CIRCUIT TRACE & TEST POINT MAPS™',
    subtitle: 'Visual Electrical Troubleshooting Guide',
    body: [
      'Turn electrical diagrams and circuit paths into structured diagnostic routes with logical test points, expected conditions and next-step decisions.',
      'Use visual circuit maps, fault-isolation pathways, control-versus-power references and ready-to-use test-point planning resources.',
    ],
    kicker: 'Trace the path. Choose the point. Test.',
  },
  {
    num: 4,
    title: 'INTERMITTENT ELECTRICAL FAULTS PRO™',
    subtitle: 'Advanced Detection & Evidence Guide',
    body: [
      'Organize the investigation of electrical faults that appear under changing load, temperature, vibration, movement, operating time or other intermittent conditions.',
      'Use event logs, evidence pathways, fault-pattern resources and structured methods to narrow difficult intermittent problems.',
    ],
    kicker: 'Capture it. Compare it. Isolate it.',
  },
]

export const differentiators = [
  {
    emoji: '🗺️',
    title: 'Ready-to-Use Visual Maps',
    body: 'Open the system and access organized troubleshooting resources instead of searching from scratch.',
  },
  {
    emoji: '⚡',
    title: 'Practical Diagnostic Pathways',
    body: 'Follow symptoms, possible causes, checks and testing sequences in a structured format.',
  },
  {
    emoji: '🔧',
    title: '6 Essential Areas',
    body: 'Keep diagnostics, measurements, circuits, protection devices, controls and real-world troubleshooting resources organized.',
  },
  {
    emoji: '🔄',
    title: 'Reusable Field References',
    body: 'Return to the maps and resources whenever you encounter relevant electrical symptoms.',
  },
  {
    emoji: '📂',
    title: 'Organized Diagnostic Library',
    body: 'Stop searching through scattered diagrams, screenshots, notes and reference material.',
  },
  {
    emoji: '⏱️',
    title: 'Less Searching',
    body: 'Spend less time finding troubleshooting information and keep more resources ready for field use.',
  },
]

export const previewImages = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/sgDUsHUJo2boQi8vWj3ozK/Captura_de_pantalla_2026-09-05_a_la(s)_10.08.46_p._m..webp',
    alt: 'Inside page — Symptom is not the root cause: motor does not start, breaker trips, intermittent load, voltage present',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/7CvG5JCM7XeXYjXkawK78i/Captura_de_pantalla_2026-09-05_a_la(s)_10.09.08_p._m..webp',
    alt: 'Inside page — Define normal operation first: control circuit with expected versus actual readings',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/vtYMYBs2HfMhgcPvgkUVvm/Captura_de_pantalla_2026-09-05_a_la(s)_10.09.25_p._m..webp',
    alt: 'Inside page — Make it safe before diagnosing: de-energization, lockout/tagout, stored energy, rated equipment',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/fXKBeopsPVyw5R2kaaX54b/Captura_de_pantalla_2026-09-05_a_la(s)_10.09.54_p._m..webp',
    alt: 'Inside page — Test one hypothesis at a time: random testing versus evidence-based testing',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/bQYMuaii9yxvz9AZt3EjS7/Captura_de_pantalla_2026-09-05_a_la(s)_10.10.14_p._m..webp',
    alt: 'Inside page — Why continuity alone can mislead: de-energized, energized no load, and under load readings',
  },
]

export interface Testimonial {
  name: string
  role: string
  quote: string
}

export const testimonials: Testimonial[] = [
  {
    name: 'Craig Bannister, 44',
    role: 'Industrial Maintenance Electrician — Leeds, United Kingdom',
    quote:
      'I look after three production lines and downtime is measured in hundreds of pounds a minute. Before this I was flicking between old wiring PDFs on my phone and a notebook full of scribbles. Now I open the control-fault pathway, work the test points in order and I know within minutes whether I am chasing a power-side or control-side problem. Last month it saved me a full shift on a contactor fault I would have swapped parts for.',
  },
  {
    name: 'Dwayne Ferrell, 37',
    role: 'Licensed Electrician — Houston, United States',
    quote:
      'Voltage drop was always where I second-guessed myself. The measurement pages lay out what to expect under load versus no load, and the continuity page alone changed how I test connections. I stopped clearing calls as "no fault found" and started coming back with an actual reading and a cause I can show the customer.',
  },
  {
    name: 'Ryan Kowalczyk, 29',
    role: 'Commercial Electrician — Calgary, Canada',
    quote:
      'Four years in and the knowledge was there, the order was not. Having the fault-isolation maps open on my tablet while I trace a circuit changed how I work — one hypothesis, one test, one result. My foreman noticed within two weeks that I stopped calling him for second opinions on breaker trips.',
  },
  {
    name: 'Malcolm Trethewey, 51',
    role: 'Maintenance Supervisor — Brisbane, Australia',
    quote:
      'I run a crew of five and consistency was my headache. They each diagnosed differently and the paperwork showed it. The reporting kit gave us one format for symptom, tests, readings and root cause, and the diagnostic maps gave the apprentices somewhere to start instead of guessing. Callbacks are down and my service records are finally defensible.',
  },
  {
    name: 'Sean Doherty, 33',
    role: 'Facilities Electrical Technician — Manchester, United Kingdom',
    quote:
      'The intermittent faults bonus is what I use most. We had a lighting circuit dropping out only in the afternoon and the event-log approach in that guide pointed straight at a thermal issue on a loose terminal. Two visits instead of the six we would normally burn through. That one job paid for the whole system many times over.',
  },
]

export const faqs = [
  {
    q: 'Is this a physical product?',
    a: 'No. ELECTRICAL DIAGNOSTIC PRO™ is a 100% digital professional resource.',
  },
  {
    q: 'Who is this for?',
    a: 'Electricians, electrical technicians, maintenance technicians and professionals looking for organized, visual and ready-to-use troubleshooting resources.',
  },
  {
    q: 'Is this a certification or training program?',
    a: 'No. It is a professional diagnostic and reference resource, not a certification or substitute for required technical education.',
  },
  {
    q: 'Is this another theory-heavy ebook?',
    a: 'No. The core system focuses on practical visual diagnostic maps, troubleshooting flows, circuit references and ready-to-use field resources.',
  },
  {
    q: 'Do I need to create the diagnostic maps myself?',
    a: 'No. The maps and resources are already created and organized. You select and use the appropriate resources according to the circuit, equipment, symptom and professional judgment.',
  },
  {
    q: 'What areas are included?',
    a: 'Electrical Diagnostic Foundations, Multimeter Testing & Measurements, Wiring & Circuit Faults, Protection Devices, Motors & Controls, and Real-World Troubleshooting Cases.',
  },
  {
    q: 'Can I use the resources with different electrical systems?',
    a: 'The resources provide general troubleshooting frameworks that can be used as references while following the specific manufacturer documentation and requirements for the equipment being serviced.',
  },
  {
    q: 'Does it include field-ready resources?',
    a: 'Yes. The system includes visual resources designed for practical digital consultation and reference.',
  },
  {
    q: 'Does it include testing and organization resources?',
    a: 'Yes. The four complementary bonuses include quick-reference diagnostics, professional reporting, circuit trace and test-point maps, and intermittent-fault investigation tools.',
  },
  {
    q: "Is it tied to one country's electrical standards?",
    a: 'No. It is an English-language professional resource designed for an international market and is not intended to replace local regulations, licensing requirements, electrical codes, safety procedures or manufacturer requirements.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
]

export interface PurchaseNotification {
  name: string
  location: string
}

export const purchaseNotifications: PurchaseNotification[] = [
  { name: 'James W.', location: 'Manchester, United Kingdom' },
  { name: 'Kyle R.', location: 'Calgary, Canada' },
  { name: 'Miguel S.', location: 'Houston, United States' },
  { name: 'Daniel T.', location: 'Brisbane, Australia' },
  { name: 'Craig M.', location: 'Leeds, United Kingdom' },
  { name: 'Brandon L.', location: 'Toronto, Canada' },
  { name: 'Shawn P.', location: 'Phoenix, United States' },
  { name: 'Nathan K.', location: 'Perth, Australia' },
  { name: 'Aaron D.', location: 'Birmingham, United Kingdom' },
  { name: 'Travis H.', location: 'Edmonton, Canada' },
  { name: 'Eric J.', location: 'Tampa, United States' },
  { name: 'Luke F.', location: 'Melbourne, Australia' },
]
